import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../i18n";
import { VILLA } from "../data";

export const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -64 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const LangToggle = ({ scrolled, className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 text-[12px] tracking-wide ${className}`} data-testid="lang-toggle">
      {["fr", "en"].map((l, i) => (
        <span key={l} className="flex items-center">
          {i === 1 && <span className={scrolled ? "text-ink-soft/50 mx-1" : "text-white/50 mx-1"}>/</span>}
          <button
            onClick={() => setLang(l)}
            data-testid={`lang-${l}`}
            className={`uppercase transition-colors ${
              lang === l
                ? scrolled ? "text-ink font-semibold" : "text-white font-semibold"
                : scrolled ? "text-ink-soft/60 hover:text-ink" : "text-white/50 hover:text-white"
            }`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 1.4, ease: [0.19, 1, 0.22, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-sand-bg/70 backdrop-blur-xl border-b border-[var(--line)]" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <button
          onClick={() => go("hero")}
          data-testid="logo-btn"
          aria-label="Villa Tutto Passa"
          className="flex items-center gap-3 md:gap-4"
        >
          <img
            src="/images/logo_beach.png"
            alt="Villa Tutto Passa"
            className={`w-auto transition-all duration-500 ${
              scrolled
                ? "h-12 md:h-16 invert"
                : "h-14 md:h-[4.5rem] md:[filter:drop-shadow(0_0_0.4px_#ffffff)]"
            }`}
          />
          <img
            src="/images/wordmark_white.png"
            alt="Tutto Passa"
            className={`w-auto transition-all duration-500 ${
              scrolled
                ? "h-6 md:h-8 invert"
                : "h-7 md:h-10 md:[filter:drop-shadow(0_0_0.4px_#ffffff)]"
            }`}
          />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {t.nav.links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-${l.id}`}
              className={`text-[15px] font-medium tracking-wide link-underline transition-colors ${
                scrolled ? "text-ink-soft hover:text-ink" : "text-white/85 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
          <LangToggle scrolled={scrolled} />
          <button
            onClick={() => go("contact")}
            data-testid="nav-contact"
            className={`text-[15px] font-medium tracking-wide px-5 py-2 border transition-all duration-300 ${
              scrolled
                ? "border-ink text-ink hover:bg-ink hover:text-sand-bg"
                : "border-white/60 text-white hover:bg-white hover:text-ink"
            }`}
          >
            {t.nav.reserve}
          </button>
        </nav>

        <div className="md:hidden flex items-center gap-5">
          <LangToggle scrolled={scrolled} />
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-btn"
            className={`text-sm ${scrolled ? "text-ink" : "text-white"}`}
          >
            {open ? t.nav.close : t.nav.menu}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-sand-bg border-b border-[var(--line)]"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {[...t.nav.links, { label: t.nav.reserve, id: "contact" }].map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-nav-${l.id}`}
                  className="text-left font-serif text-2xl text-ink"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
